"use client";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

interface BodyContentClientProps {
    fullText: string;
    className?: string;
    additionalContent?: React.ReactNode;
    contecttextclass?: string;
}

const PLAIN_TEXT_READ_MORE_THRESHOLD = 100;

const getPlainTextLength = (html: string) =>
    html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().length;

const hasBlockHtml = (html: string) => /<p[\s>]/i.test(html);

const measureOverflow = (
    el: HTMLDivElement,
    fullText: string,
    additionalContent?: React.ReactNode,
) => {
    if (additionalContent) return true;
    if (getPlainTextLength(fullText) > PLAIN_TEXT_READ_MORE_THRESHOLD) return true;
    return el.scrollHeight > el.clientHeight + 1;
};

const BodyContentClient: React.FC<BodyContentClientProps> = ({ fullText, className = "line-clamp-3", additionalContent,contecttextclass }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    // Start true so SSR/initial HTML renders the button immediately (prevents CLS).
    // useLayoutEffect corrects it to false synchronously for short content.
    const [isOverflowing, setIsOverflowing] = useState(true);
    const contentRef = useRef<HTMLDivElement>(null);
    // Ref so toggling Read More/Less doesn't recreate the observer below.
    const isExpandedRef = useRef(isExpanded);
    isExpandedRef.current = isExpanded;
    const useMaxHeightCollapse = hasBlockHtml(fullText);

    // Synchronous initial measurement to avoid CLS (button popping in after paint)
    useLayoutEffect(() => {
        const el = contentRef.current;
        if (!el) return;
        setIsOverflowing(measureOverflow(el, fullText, additionalContent));
    }, [fullText, additionalContent]);

    // ResizeObserver for subsequent layout changes (only while collapsed)
    useEffect(() => {
        const el = contentRef.current;
        if (!el) return;

        const ro = new ResizeObserver(() => {
            if (!isExpandedRef.current) {
                setIsOverflowing(measureOverflow(el, fullText, additionalContent));
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [fullText, additionalContent]);

    const collapsedContentClass = useMaxHeightCollapse
        ? "max-h-[66px] md:max-h-[44px]"
        : `${className} md:line-clamp-2`;

    return (
        <div className={`font-roboto font-normal text-[15px] leading-[22px] text-[#6B7280] ${(isOverflowing || isExpanded) ? "min-h-[96px] md:min-h-[74px]" : ""} ${contecttextclass}`}>
            <div
                ref={contentRef}
                className={`body-content-wrapper transition-all duration-300 ease-in-out overflow-hidden ${isExpanded
                        ? "max-h-none line-clamp-none"
                        : collapsedContentClass
                    }`}
                dangerouslySetInnerHTML={{ __html: fullText }}
            />

            {!!additionalContent && (
                <div className={`mt-4 ${(!isExpanded && isOverflowing) ? "hidden" : "block"}`}>
                    {additionalContent}
                </div>
            )}

            {/* Render button only when content is overflowing or when expanded */}
            {(isOverflowing || isExpanded) && (
                <div className="mt-2" aria-hidden={!(isOverflowing || isExpanded)}>
                    <button
                        type="button"
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="text-[#007AFF] font-medium focus:outline-none cursor-pointer"
                    >
                        {isExpanded ? "Read Less" : "Read More"}
                    </button>
                </div>
            )}
        </div>
    );
};

export default BodyContentClient;
